/**
 * Форматировщик телефонных номеров
 * Обрабатывает форматирование номеров согласно маскам конкретных стран
 */
export class PhoneFormatter {
  private pattern: string = ''
  // E.164: номер с кодом страны — не длиннее 15 цифр.
  private fallbackDigitsLimit: number = 15

  /**
   * Установить паттерн форматирования
   * @param {string} pattern - Шаблон форматирования
   */
  public setPattern(pattern: string): void {
    this.pattern = pattern
  }

  /**
   * Получить текущий паттерн
   * @return {string} Текущий паттерн форматирования
   */
  public getPattern(): string {
    return this.pattern
  }

  /**
   * Получить максимально допустимое количество цифр
   * @return {number} Лимит цифр
   */
  public getMaxDigits(): number {
    if (!this.pattern) {
      return this.fallbackDigitsLimit
    }

    return (this.pattern.match(/[0#]/g) || []).length
  }

  /**
   * Отформатировать введённое значение согласно маске
   * @param {string} value - Исходное введённое значение телефона
   * @return {string} Отформатированный номер телефона
   */
  public format(value: string): string {
    const digits = value.replace(/\D/g, '')
    const maxDigits = this.getMaxDigits()
    const limitedDigits = digits.slice(0, maxDigits)

    if (!this.pattern) {
      return limitedDigits
    }

    let index = 0

    return this.pattern
      .split('')
      .map((char) => {
        if (char === '0' || char === '#') {
          if (index >= limitedDigits.length) {
            return ''
          }

          const digit = limitedDigits.charAt(index)
          index += 1
          return digit
        }

        if (
          limitedDigits.length > 0 &&
          (index === 0 || index < limitedDigits.length)
        ) {
          return char
        }

        return ''
      })
      .join('')
  }

  /**
   * Сгенерировать placeholder из маски
   * @return {string} Текст placeholder для поля ввода
   */
  public getPlaceholder(): string {
    return PhoneFormatter.placeholderFromPattern(this.pattern)
  }

  /**
   * Построить placeholder из произвольной маски
   * @param {string} pattern - Шаблон форматирования
   * @return {string} Текст placeholder; пустая строка, если маски нет
   */
  public static placeholderFromPattern(pattern: string): string {
    return pattern.replace(/[0#]/g, '9')
  }

  /**
   * Вычислить новую позицию курсора после форматирования
   * @param {string} oldValue - Предыдущее значение до форматирования
   * @param {string} newValue - Новое значение после форматирования
   * @param {number} oldCursorPosition - Позиция курсора до форматирования
   * @return {number} Новая позиция курсора
   */
  public calculateCursorPosition(
    oldValue: string,
    newValue: string,
    oldCursorPosition: number
  ): number {
    const oldLength = oldValue.length
    const newLength = newValue.length
    const diff = newLength - oldLength
    return Math.max(0, oldCursorPosition + diff)
  }

  /**
   * Посчитать количество цифр до позиции курсора
   * @param {string} value - Строка значения поля
   * @param {number} cursorPosition - Позиция курсора
   * @return {number} Количество цифр до курсора
   */
  public countDigitsBeforeCursor(
    value: string,
    cursorPosition: number
  ): number {
    const limitedCursor = Math.max(0, Math.min(cursorPosition, value.length))
    return (value.slice(0, limitedCursor).match(/\d/g) || []).length
  }

  /**
   * Получить позицию курсора после N-й цифры
   * @param {string} value - Отформатированное значение
   * @param {number} digitIndex - Индекс цифры для позиционирования
   * @return {number} Позиция курсора
   */
  public getCursorPositionForDigitIndex(
    value: string,
    digitIndex: number
  ): number {
    const normalizedDigitIndex = Math.max(0, digitIndex)

    if (normalizedDigitIndex === 0) {
      return value.startsWith('+') ? 1 : 0
    }

    let digitsSeen = 0
    for (let index = 0; index < value.length; index += 1) {
      if (/\d/.test(value.charAt(index))) {
        digitsSeen += 1
        if (digitsSeen >= normalizedDigitIndex) {
          return index + 1
        }
      }
    }

    return value.length
  }
}
